import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogContent,
  IconButton,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  CameraAlt as PhotoIcon,
  Straighten as MeasurementIcon,
  Notes as NoteIcon,
  ListAlt as SelectionIcon,
  HelpOutline as QuestionIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  AddPhotoAlternate as AttachPhotoIcon,
} from '@mui/icons-material';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import DisplayMedia from '../../../components/DisplayMedia';
import { AiGuidance } from '../../settings/components/TaskListManagement/aiguidanceUtils';

const getTaskListItemsQuery = /* GraphQL */ `
  query GetTaskListItems(
    $dealID: String!
    $taskListID: String
    $userID: String
    $autoCreate: Boolean
  ) {
    getTaskListItems(
      dealID: $dealID
      taskListID: $taskListID
      userID: $userID
      autoCreate: $autoCreate
    )
  }
`;

interface Measurement {
  key: string;
  value: number | null;
  unit: string;
  createdAt: string | null;
}

interface Utterance {
  text: string;
  actionType: string;
  confidence: number | null;
  createdAt: string | null;
}

interface TaskListItem {
  id: string;
  name: string;
  description: string | null;
  taskType: string;
  itemKey: string;
  isCompleted: boolean;
  isSkipped: boolean;
  sortOrder: number;
  unitOfMeasure: string;
  userTranscript: string | null;
  aiTranscript: string | null;
  measurements: Measurement[];
  utterances: Utterance[];
  aiGuidance?: AiGuidance | string | null;
  imageName?: string | null;
}

interface TaskList {
  id: string;
  dealId: string;
  templateName: string;
  templateDescription: string | null;
  isCompleted: boolean;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  items: TaskListItem[];
}

interface DealFile {
  id: string;
  url?: string;
  fileName?: string;
  type?: string;
  tags?: string[];
  task_id?: string;
  description?: string;
  isDeleted?: boolean;
}

interface DealInspectionQAProps {
  dealID: string;
  dealFiles?: DealFile[];
}

type StatusFilter = 'all' | 'completed' | 'incomplete';

const TASK_TYPE_ICONS: Record<string, React.ReactNode> = {
  photo: <PhotoIcon fontSize="small" />,
  measurement: <MeasurementIcon fontSize="small" />,
  note: <NoteIcon fontSize="small" />,
  selection: <SelectionIcon fontSize="small" />,
  question: <QuestionIcon fontSize="small" />,
};

const TASK_TYPE_LABELS: Record<string, string> = {
  photo: 'Photo',
  question: 'Question',
  measurement: 'Measurement',
  note: 'Note',
  selection: 'Selection',
};

const SampleImageSection: React.FC<{ imageKey: string; theme: any }> = ({
  imageKey,
  theme,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    Storage.get(imageKey)
      .then((resolved) => {
        if (!cancelled) {
          setUrl(typeof resolved === 'string' ? resolved : String(resolved));
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [imageKey]);

  if (failed) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        sx={{
          color: theme.textLight,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          mb: 0.5,
        }}
      >
        Sample Image
      </Typography>
      {loading ? (
        <CircularProgress size={20} sx={{ color: theme.primary }} />
      ) : (
        url && (
          <Box
            component="img"
            src={url}
            alt="Sample"
            onError={() => setFailed(true)}
            sx={{
              maxWidth: 200,
              maxHeight: 150,
              borderRadius: '6px',
              objectFit: 'cover',
              border: `1px solid ${theme.stroke}`,
            }}
          />
        )
      )}
    </Box>
  );
};

const DealInspectionQA: React.FC<DealInspectionQAProps> = ({
  dealID,
  dealFiles = [],
}) => {
  const { theme } = useTheme();
  const [taskLists, setTaskLists] = useState<TaskList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [downloadingTaskId, setDownloadingTaskId] = useState<string | null>(
    null
  );
  const [uploadingFiles, setUploadingFiles] = useState<
    Record<string, { name: string; taskId: string }[]>
  >({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [activeUploadTaskId, setActiveUploadTaskId] = useState<string | null>(
    null
  );
  const [localUploadedFiles, setLocalUploadedFiles] = useState<DealFile[]>([]);
  const [fetchedDealFiles, setFetchedDealFiles] = useState<DealFile[]>([]);

  // Fetch deal files on mount to get latest (including previously uploaded photos)
  const fetchDealFiles = useCallback(async () => {
    if (!dealID) return;
    try {
      const orgId = (global as any)?.me?.orgID;
      const filter: any = { isDeleted: { ne: true } };
      if (orgId) {
        filter.orgID = { eq: orgId };
      }
      const result: any = await API.graphql({
        query: `query ListDealFiles($dealID: ID!, $filter: ModelDealFileFilterInput) {
          listDealFilesByDealIDType(dealID: $dealID, filter: $filter) {
            items { id dealID url type tags task_id description isDeleted }
          }
        }`,
        variables: { dealID, filter },
      });
      const files = result?.data?.listDealFilesByDealIDType?.items || [];
      setFetchedDealFiles(files.filter((f: any) => f && !f.isDeleted));
    } catch (err) {
      console.error('[DealInspectionQA] Failed to fetch deal files:', err);
    }
  }, [dealID]);

  useEffect(() => {
    fetchDealFiles();
  }, [fetchDealFiles]);

  const fetchTaskLists = useCallback(async () => {
    if (!dealID) return;
    setIsLoading(true);
    setError(null);
    try {
      const userID = (global as any)?.me?.id;

      // First call: get the list of task lists (may not include items)
      const result: any = await API.graphql(
        graphqlOperation(getTaskListItemsQuery, {
          dealID,
          userID,
          autoCreate: false,
        })
      );
      const raw = result.data.getTaskListItems;
      let json = raw;
      while (typeof json === 'string') {
        try {
          json = JSON.parse(json);
        } catch {
          break;
        }
      }

      if (json.success && json.data?.taskLists?.length > 0) {
        const lists = json.data.taskLists;

        // If items are already populated, use them directly
        if (lists[0].items?.length > 0) {
          setTaskLists(lists);
          return;
        }

        // Otherwise fetch each task list individually with taskListID to get items
        const fullLists = await Promise.all(
          lists.map(async (tl: any) => {
            try {
              const tlResult: any = await API.graphql(
                graphqlOperation(getTaskListItemsQuery, {
                  dealID,
                  taskListID: tl.id,
                  userID,
                })
              );
              let tlJson = tlResult.data.getTaskListItems;
              while (typeof tlJson === 'string') {
                try {
                  tlJson = JSON.parse(tlJson);
                } catch {
                  break;
                }
              }
              if (tlJson.success && tlJson.data?.taskLists?.[0]) {
                return tlJson.data.taskLists[0];
              }
            } catch (err) {
              console.error(
                '[DealInspectionQA] Error fetching task list:',
                tl.id,
                err
              );
            }
            return tl;
          })
        );

        setTaskLists(fullLists);
      } else {
        setTaskLists([]);
      }
    } catch (err: any) {
      console.error('[DealInspectionQA] Fetch error:', err);
      setError(err.message || 'Failed to load inspection data');
    } finally {
      setIsLoading(false);
    }
  }, [dealID]);

  useEffect(() => {
    fetchTaskLists();
  }, [fetchTaskLists]);

  // ── Photo upload handler ──────────────────────────────────

  const createDealFileMutation = /* GraphQL */ `
    mutation CreateDealFile($input: CreateDealFileInput!) {
      createDealFile(input: $input) {
        id
        dealID
        url
        type
        tags
        description
        createdAt
        isDeleted
      }
    }
  `;

  const handleAttachPhoto = useCallback((taskId: string) => {
    setActiveUploadTaskId(taskId);
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0 || !activeUploadTaskId) return;

      const taskId = activeUploadTaskId;
      const orgId = (global as any)?.me?.orgID;
      const userId = (global as any)?.me?.id;

      // Show uploading placeholders
      const placeholders = Array.from(files).map((f) => ({
        name: f.name,
        taskId,
      }));
      setUploadingFiles((prev) => ({
        ...prev,
        [taskId]: [...(prev[taskId] || []), ...placeholders],
      }));

      const uploadedDealFiles: DealFile[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const timestamp = Date.now();
          const s3Key = `deal-photos/${dealID}/${taskId}/${timestamp}-${file.name}`;

          await Storage.put(s3Key, file, {
            contentType: file.type || 'image/jpeg',
            level: 'public',
          });

          // Create DealFile record
          const fileTimestamp = Date.now();
          await API.graphql({
            query: createDealFileMutation,
            variables: {
              input: {
                id: s3Key,
                orgID: orgId,
                dealID,
                deal_id: dealID,
                type: file.type || 'image/jpeg',
                url: s3Key,
                tags: [],
                task_id: taskId,
                description: '',
                sequence: i + 1,
                createdBy: userId,
                lastUpdatedBy: userId,
                isDeleted: false,
                fileTimestampEpoch: fileTimestamp,
              },
            },
          });

          // Track for immediate UI
          uploadedDealFiles.push({
            id: s3Key,
            url: s3Key,
            type: file.type || 'image/jpeg',
            task_id: taskId,
            tags: [],
            isDeleted: false,
          });
        } catch (err) {
          console.error('[DealInspectionQA] Photo upload failed:', err);
        }
      }

      // Remove placeholders, add uploaded files to local state
      setUploadingFiles((prev) => {
        const updated = { ...prev };
        delete updated[taskId];
        return updated;
      });

      if (uploadedDealFiles.length > 0) {
        setLocalUploadedFiles((prev) => [...prev, ...uploadedDealFiles]);
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setActiveUploadTaskId(null);
    },
    [activeUploadTaskId, dealID, createDealFileMutation]
  );

  const photosByTaskId = useMemo(() => {
    const map: Record<string, DealFile[]> = {};
    const allFiles = [...dealFiles, ...fetchedDealFiles, ...localUploadedFiles];
    // Deduplicate by ID
    const seen = new Set<string>();
    const validFiles = allFiles.filter(
      (f) =>
        f &&
        !f.isDeleted &&
        f.type?.startsWith('image/') &&
        (f.url || f.fileName || f.id) &&
        !seen.has(f.id) &&
        (seen.add(f.id), true)
    );
    for (const file of validFiles) {
      // Match by task_id (direct association, like mobile)
      if (file.task_id) {
        if (!map[file.task_id]) map[file.task_id] = [];
        map[file.task_id].push(file);
      }
      // Also match by tags (legacy: tag matches task name/itemKey)
      if (Array.isArray(file.tags)) {
        for (const tag of file.tags) {
          if (!map[tag]) map[tag] = [];
          map[tag].push(file);
        }
      }
    }
    return map;
  }, [dealFiles, fetchedDealFiles, localUploadedFiles]);

  const allTaskTypes = useMemo(() => {
    const types = new Set<string>();
    for (const tl of taskLists) {
      for (const item of tl.items || []) {
        types.add(item.taskType);
      }
    }
    return Array.from(types);
  }, [taskLists]);

  const filterItems = useCallback(
    (items: TaskListItem[]) => {
      let filtered = items;
      if (statusFilter === 'completed') {
        filtered = filtered.filter((i) => i.isCompleted);
      } else if (statusFilter === 'incomplete') {
        filtered = filtered.filter((i) => !i.isCompleted);
      }
      if (selectedTaskType) {
        filtered = filtered.filter((i) => i.taskType === selectedTaskType);
      }
      return filtered;
    },
    [statusFilter, selectedTaskType]
  );

  const formatMeasurement = (m: Measurement) => {
    if (m.value == null) return '—';
    const unitLabel = m.unit?.replace(/_/g, ' ') || '';
    return `${m.value} ${unitLabel}`.trim();
  };

  const getPhotosForTask = (item: TaskListItem): DealFile[] => {
    return (
      photosByTaskId[item.id] ||
      photosByTaskId[item.name] ||
      photosByTaskId[item.itemKey] ||
      []
    );
  };

  const handleDownloadTaskPhotos = async (item: TaskListItem) => {
    const photos = getPhotosForTask(item);
    if (photos.length === 0) return;
    setDownloadingTaskId(item.id);
    try {
      const zip = new JSZip();
      let count = 0;
      for (const file of photos) {
        try {
          const key = file.url || file.fileName || file.id;
          const signedUrl = await Storage.get(key);
          const res = await fetch(signedUrl);
          const blob = await res.blob();
          const name = key.split('/').pop() || `photo-${count}`;
          zip.file(name, blob);
          count++;
        } catch {}
      }
      if (count > 0) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const safeName = (item.name || item.itemKey)
          .replace(/[^a-zA-Z0-9]/g, '_')
          .substring(0, 30);
        saveAs(zipBlob, `${safeName}_photos.zip`);
      }
    } catch (err) {
      console.error('Error downloading task photos:', err);
    } finally {
      setDownloadingTaskId(null);
    }
  };

  const renderSampleImage = (item: TaskListItem) => {
    const key = item.imageName;
    if (!key || key.trim() === '') return null;
    return <SampleImageSection imageKey={key} theme={theme} />;
  };

  const renderTaskPhotos = (item: TaskListItem) => {
    const photos = getPhotosForTask(item);
    if (photos.length === 0) return null;

    const isDownloading = downloadingTaskId === item.id;
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {photos.map((file) => {
            const imageName = file.url || file.fileName || file.id;
            return (
              <Box
                key={file.id}
                onClick={() => setLightboxImage(imageName)}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: `1px solid ${theme.stroke}`,
                  '&:hover': { opacity: 0.85 },
                }}
              >
                <DisplayMedia
                  imageName={imageName}
                  imageType="image"
                  height={80}
                  width={80}
                  borderRadius={6}
                  objectFit="cover"
                />
              </Box>
            );
          })}
        </Box>
        <Button
          onClick={() => handleDownloadTaskPhotos(item)}
          disabled={isDownloading}
          size="small"
          startIcon={
            isDownloading ? (
              <CircularProgress size={14} sx={{ color: theme.primary }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            mt: 1,
            color: theme.primary,
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 500,
            px: 1,
            '&:hover': { backgroundColor: `${theme.primary}10` },
          }}
        >
          {isDownloading
            ? 'Downloading...'
            : `Download Photos (${photos.length})`}
        </Button>
      </Box>
    );
  };

  const renderTranscript = (text: string, variant: 'user' | 'ai' = 'user') => (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        backgroundColor:
          variant === 'user' ? `${theme.primary}08` : `${theme.textLight}08`,
        borderLeft: `3px solid ${
          variant === 'user' ? theme.primary : theme.textLight
        }`,
        borderRadius: '0 6px 6px 0',
      }}
    >
      <Typography sx={{ color: theme.text, fontSize: 14, lineHeight: 1.5 }}>
        {text}
      </Typography>
    </Box>
  );

  const renderAnswer = (item: TaskListItem) => {
    const hasUserTranscript = !!item.userTranscript;
    const hasAiTranscript = !!item.aiTranscript;
    const hasUtterances = item.utterances?.length > 0;
    const hasMeasurements = item.measurements?.length > 0;

    if (item.isSkipped) {
      return (
        <Chip
          label="Skipped"
          size="small"
          sx={{
            mt: 0.5,
            backgroundColor: `${theme.textLight}15`,
            color: theme.textLight,
            fontWeight: 500,
            fontSize: 12,
          }}
        />
      );
    }

    if (item.taskType === 'question') {
      if (hasUserTranscript) {
        return renderTranscript(item.userTranscript!, 'user');
      }
      if (hasUtterances) {
        return (
          <Box
            sx={{
              mt: 0.5,
              p: 1.5,
              backgroundColor: `${theme.primary}08`,
              borderLeft: `3px solid ${theme.primary}`,
              borderRadius: '0 6px 6px 0',
            }}
          >
            {item.utterances.map((u, i) => (
              <Typography
                key={i}
                sx={{ color: theme.text, fontSize: 14, lineHeight: 1.5 }}
              >
                {u.text}
              </Typography>
            ))}
          </Box>
        );
      }
    }

    if (hasUserTranscript) {
      return renderTranscript(item.userTranscript!, 'user');
    }

    if (hasMeasurements) {
      return item.measurements.map((m, i) => (
        <Typography key={i} sx={{ color: theme.text, fontSize: 14, mt: 0.5 }}>
          {formatMeasurement(m)}
        </Typography>
      ));
    }

    if (hasUtterances) {
      return (
        <Box sx={{ mt: 0.5 }}>
          {item.utterances.map((u, i) => (
            <Typography key={i} sx={{ color: theme.text, fontSize: 14 }}>
              {u.text}
            </Typography>
          ))}
        </Box>
      );
    }

    if (hasAiTranscript) {
      return renderTranscript(item.aiTranscript!, 'ai');
    }

    if (item.isCompleted) {
      return (
        <Chip
          label="Completed"
          size="small"
          sx={{
            mt: 0.5,
            backgroundColor: `${theme.primary}20`,
            color: theme.primary,
            fontWeight: 500,
            fontSize: 12,
          }}
        />
      );
    }

    return (
      <Typography
        sx={{
          color: theme.textLight,
          fontSize: 14,
          mt: 0.5,
          fontStyle: 'italic',
        }}
      >
        No response yet
      </Typography>
    );
  };

  const renderTaskItem = (item: TaskListItem, taskListCompleted: boolean) => (
    <Box
      key={item.id}
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.5,
        borderBottom: `1px solid ${theme.stroke}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          mt: 0.25,
          color: item.isSkipped
            ? theme.textLight
            : item.isCompleted
            ? theme.primary
            : theme.textLight,
          opacity: item.isSkipped ? 0.5 : 1,
        }}
      >
        {item.isCompleted ? (
          <CheckCircleIcon fontSize="small" />
        ) : (
          <UncheckedIcon fontSize="small" />
        )}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>
            {item.name || item.itemKey}
          </Typography>
          {!taskListCompleted && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachPhotoIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleAttachPhoto(item.id);
              }}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                py: 0.25,
                px: 1,
                minWidth: 'auto',
                borderColor: theme.stroke,
                color: theme.textLight,
                '&:hover': {
                  borderColor: theme.primary,
                  color: theme.primary,
                  backgroundColor: `${theme.primary}08`,
                },
              }}
            >
              Add Photo
            </Button>
          )}
          {TASK_TYPE_LABELS[item.taskType] && item.taskType !== 'photo' && (
            <Chip
              label={TASK_TYPE_LABELS[item.taskType]}
              size="small"
              sx={{
                fontSize: 10,
                height: 20,
                backgroundColor: `${theme.textLight}15`,
                color: theme.textLight,
              }}
            />
          )}
        </Box>

        {item.description && (
          <Typography sx={{ color: theme.textLight, fontSize: 12, mt: 0.25 }}>
            {item.description}
          </Typography>
        )}

        {item.aiGuidance &&
          (() => {
            const ag = item.aiGuidance;
            if (typeof ag === 'string') {
              // Legacy plain-text fallback
              return (
                <Typography
                  data-testid="aiguidance-section"
                  sx={{
                    color: theme.textLight,
                    fontSize: 12,
                    mt: 0.25,
                    fontStyle: 'italic',
                  }}
                >
                  {ag}
                </Typography>
              );
            }
            const hasAny =
              ag.photo_evidence ||
              (ag.speech_signals?.length ?? 0) > 0 ||
              ag.completion_criteria;
            if (!hasAny) return null;
            return (
              <Box
                data-testid="aiguidance-section"
                sx={{
                  mt: 0.75,
                  p: 1.25,
                  borderLeft: `3px solid ${theme.textLight}40`,
                  borderRadius: '0 6px 6px 0',
                  backgroundColor: `${theme.textLight}08`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                {ag.photo_evidence && (
                  <Box>
                    <Typography
                      sx={{
                        color: theme.textLight,
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      Photo Evidence
                    </Typography>
                    <Typography
                      sx={{ color: theme.text, fontSize: 12, mt: 0.15 }}
                    >
                      {ag.photo_evidence}
                    </Typography>
                  </Box>
                )}
                {ag.speech_signals && ag.speech_signals.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        color: theme.textLight,
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      Speech Signals
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      {ag.speech_signals.map((s, i) => (
                        <Chip
                          key={i}
                          label={s}
                          size="small"
                          sx={{
                            fontSize: 11,
                            height: 20,
                            backgroundColor: `${theme.textLight}15`,
                            color: theme.textLight,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {ag.completion_criteria && (
                  <Box>
                    <Typography
                      sx={{
                        color: theme.textLight,
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      Completion Criteria
                    </Typography>
                    <Typography
                      sx={{ color: theme.text, fontSize: 12, mt: 0.15 }}
                    >
                      {ag.completion_criteria}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })()}

        {renderSampleImage(item)}
        {renderAnswer(item)}
        {renderTaskPhotos(item)}

        {/* Uploading placeholders */}
        {(uploadingFiles[item.id] || []).length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {uploadingFiles[item.id].map((f, idx) => (
              <Box
                key={`uploading-${idx}`}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  backgroundColor: theme.background2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={14} sx={{ color: theme.textLight }} />
                <Typography
                  sx={{ fontSize: 8, color: theme.textLight, mt: 0.5 }}
                >
                  Uploading
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderFilters = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          sx={{ color: theme.textLight, fontSize: 12, fontWeight: 600 }}
        >
          Status:
        </Typography>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, val) => val && setStatusFilter(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontSize: 12,
              px: 1.5,
              py: 0.25,
              color: theme.textLight,
              borderColor: theme.stroke,
              '&.Mui-selected': {
                backgroundColor: `${theme.primary}15`,
                color: theme.primary,
                borderColor: theme.primary,
                '&:hover': { backgroundColor: `${theme.primary}25` },
              },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="incomplete">Incomplete</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {allTaskTypes.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{ color: theme.textLight, fontSize: 12, fontWeight: 600 }}
          >
            Type:
          </Typography>
          <Chip
            label="All"
            size="small"
            onClick={() => setSelectedTaskType(null)}
            sx={{
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              backgroundColor: !selectedTaskType
                ? `${theme.primary}20`
                : `${theme.textLight}10`,
              color: !selectedTaskType ? theme.primary : theme.textLight,
              border: !selectedTaskType
                ? `1px solid ${theme.primary}`
                : `1px solid ${theme.stroke}`,
            }}
          />
          {allTaskTypes.map((type) => (
            <Chip
              key={type}
              icon={
                <Box sx={{ display: 'flex', ml: 0.5, color: 'inherit' }}>
                  {TASK_TYPE_ICONS[type]}
                </Box>
              }
              label={TASK_TYPE_LABELS[type] || type}
              size="small"
              onClick={() =>
                setSelectedTaskType(selectedTaskType === type ? null : type)
              }
              sx={{
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                backgroundColor:
                  selectedTaskType === type
                    ? `${theme.primary}20`
                    : `${theme.textLight}10`,
                color:
                  selectedTaskType === type ? theme.primary : theme.textLight,
                border:
                  selectedTaskType === type
                    ? `1px solid ${theme.primary}`
                    : `1px solid ${theme.stroke}`,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  const renderTaskList = (taskList: TaskList, index: number) => {
    const items = taskList.items || [];
    const filteredItems = filterItems(items);
    const completedCount = items.filter((i) => i.isCompleted).length;
    const totalCount = items.length;

    return (
      <Accordion
        key={taskList.id}
        defaultExpanded={index === 0}
        sx={{
          backgroundColor: theme.background2,
          border: `1px solid ${theme.stroke}`,
          borderRadius: '8px !important',
          mb: 1.5,
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: theme.textLight }} />}
          sx={{ px: 2 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{ color: theme.text, fontWeight: 600, fontSize: 15 }}
            >
              {taskList.templateName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={taskList.isCompleted ? 'Completed' : 'In Progress'}
                size="small"
                sx={{
                  backgroundColor: taskList.isCompleted
                    ? `${theme.primary}20`
                    : `${theme.textLight}20`,
                  color: taskList.isCompleted ? theme.primary : theme.textLight,
                  fontWeight: 500,
                  fontSize: 11,
                  height: 22,
                }}
              />
              <Typography sx={{ color: theme.textLight, fontSize: 12 }}>
                {completedCount}/{totalCount} tasks
              </Typography>
              {taskList.createdAt && (
                <Typography sx={{ color: theme.textLight, fontSize: 12 }}>
                  {new Date(taskList.createdAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0 }}>
          {filteredItems.length > 0 ? (
            filteredItems.map((item) =>
              renderTaskItem(item, taskList.isCompleted)
            )
          ) : (
            <Typography
              sx={{
                color: theme.textLight,
                fontSize: 13,
                py: 2,
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              No tasks match the selected filters
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderLightbox = () => (
    <Dialog
      open={!!lightboxImage}
      onClose={() => setLightboxImage(null)}
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={() => setLightboxImage(null)}
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        {lightboxImage && (
          <DisplayMedia
            imageName={lightboxImage}
            imageType="image"
            height={600}
            width={800}
            borderRadius={8}
            objectFit="contain"
          />
        )}
      </DialogContent>
    </Dialog>
  );

  const renderEmpty = () => (
    <Box
      sx={{
        py: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography sx={{ color: theme.textLight, fontSize: 14 }}>
        No inspection data available for this deal.
      </Typography>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: theme.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: '#e53935', fontSize: 14 }}>{error}</Typography>
      </Box>
    );
  }

  if (taskLists.length === 0) return renderEmpty();

  return (
    <Box sx={{ py: 1 }}>
      {renderFilters()}
      {taskLists.map((tl, i) => renderTaskList(tl, i))}
      {renderLightbox()}

      {/* Hidden file input for photo uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
    </Box>
  );
};

export default DealInspectionQA;
